import React, { useCallback, useContext, useEffect, useState } from "react";
import moment from "moment";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";

const LOGIN_IDS = [10253, 10406, 10130, 10354, 10321];

const emptyReportState = {
  employees: [],
  attendanceRows: [],
  leaveRows: [],
  salaryRows: [],
  commissionRows: [],
  fineRows: [],
  advanceRows: [],
  loanRows: [],
  promotionRows: [],
  employeeLedgerRows: [],
  summary: {
    employees: 0,
    present: 0,
    late: 0,
    absent: 0,
    onLeave: 0,
    netSalary: 0,
    grossCommission: 0,
    fines: 0,
    advances: 0,
    loanOutstanding: 0,
  },
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
    maximumFractionDigits: 0,
  }).format(toNumber(value));

const formatDate = (value) => (value ? moment(value).format("DD MMM YYYY") : "N/A");

const formatDateTime = (value, inputFormat) => {
  if (!value) return "N/A";
  const parsed = inputFormat ? moment(value, inputFormat) : moment(value);
  return parsed.isValid() ? parsed.format("hh:mm A") : value;
};

const buildEmployeeName = (employee = {}, fallback = "N/A") => {
  const name = `${employee.first_name || ""} ${employee.last_name || ""}`.trim();
  return name || fallback;
};

const normalizeRows = (response) => {
  const payload = response?.data?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const safeRequestRows = async (requestPromise) => {
  try {
    const response = await requestPromise;
    return normalizeRows(response);
  } catch (error) {
    if (error?.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

const safeOptionalRows = async (label, requestPromise) => {
  try {
    const response = await requestPromise;
    return {
      rows: normalizeRows(response),
      failed: false,
      label,
    };
  } catch (error) {
    console.error(`HR report optional dataset failed: ${label}`, error);
    return {
      rows: [],
      failed: true,
      label,
    };
  }
};

const buildDutyHoursMap = (staffRows) => {
  const map = {};

  staffRows.forEach((staff) => {
    if (!Array.isArray(staff.duty_hours)) return;

    map[String(staff.id)] = {};
    staff.duty_hours.forEach((slot) => {
      if (String(slot.status) === "1") {
        map[String(staff.id)][slot.day] = {
          start_time: slot.start_time,
          end_time: slot.end_time,
        };
      }
    });
  });

  return map;
};

const SummaryTile = ({ label, value, tone }) => (
  <div className="col-12 col-sm-6 col-xl-2 mb-3">
    <div className={`card radius-10 custom-shadow border-${tone} h-100`}>
      <div className="card-body">
        <p className="text-muted mb-2">{label}</p>
        <h4 className={`mb-0 text-${tone}`}>{value}</h4>
      </div>
    </div>
  </div>
);

const DetailItem = ({ label, value }) => (
  <div className="col-md-4 col-sm-6 mb-3">
    <div className="border rounded px-3 py-2 h-100 bg-light">
      <div className="small text-muted">{label}</div>
      <div className="fw-semibold">{value || "N/A"}</div>
    </div>
  </div>
);

const ReportTableCard = ({ title, tone, rows, columns, emptyMessage }) => (
  <div className="card radius-10 custom-shadow mb-4">
    <div className={`card-header bg-white border-bottom border-${tone}`}>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h5 className={`mb-0 text-${tone}`}>{title}</h5>
        <span className={`badge bg-${tone}`}>Records: {rows.length}</span>
      </div>
    </div>
    <div className="card-body">
      {rows.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-striped table-hover align-middle">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.label}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id || row.record_key || `${title}-${index}`}>
                  {columns.map((column) => (
                    <td key={`${column.label}-${row.id || index}`}>
                      {column.render ? column.render(row, index) : row[column.key] || "N/A"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center text-muted py-4">{emptyMessage}</div>
      )}
    </div>
  </div>
);

const ScopePill = ({ label, value }) => (
  <div className="d-flex align-items-center gap-2 flex-wrap">
    <span className="small text-muted">{label}</span>
    <span className="badge text-bg-light border">{value || "All"}</span>
  </div>
);

const DetailedHRReport = () => {
  const { state } = useContext(NoteState);
  const { branch_id } = state;

  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);

  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedDesignation, setSelectedDesignation] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [startDate, setStartDate] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [warningMessages, setWarningMessages] = useState([]);
  const [reportData, setReportData] = useState(emptyReportState);

  const activeBranch = selectedBranch || branch_id || "";
  const selectedEmployeeProfile = reportData.employees.find(
    (employee) => String(employee.id) === String(selectedEmployee)
  );
  const reportRangeStart = moment(startDate).isAfter(endDate) ? endDate : startDate;
  const reportRangeEnd = moment(startDate).isAfter(endDate) ? startDate : endDate;
  const selectedBranchLabel =
    branches.find((branch) => String(branch.id) === String(activeBranch))?.name || "All Branches";
  const selectedDepartmentLabel =
    departments.find((department) => String(department.id) === String(selectedDepartment))?.name ||
    "All Departments";
  const selectedDesignationLabel =
    designations.find((designation) => String(designation.id) === String(selectedDesignation))
      ?.name || "All Designations";
  const selectedEmployeeLabel =
    employeeOptions.find((employee) => String(employee.id) === String(selectedEmployee))
      ? buildEmployeeName(
          employeeOptions.find((employee) => String(employee.id) === String(selectedEmployee))
        )
      : "All Employees";

  const fetchLookups = async () => {
    setFiltersLoading(true);
    try {
      const [branchResponse, departmentResponse, designationResponse] = await Promise.all([
        UserServices.branchName(),
        UserServices.departmentName(),
        UserServices.designationName(),
      ]);

      setBranches(branchResponse?.data?.data || []);
      setDepartments(departmentResponse?.data?.data || []);
      setDesignations(designationResponse?.data?.data || []);
    } catch (error) {
      console.error("Error loading HR report lookups:", error);
    } finally {
      setFiltersLoading(false);
    }
  };

  const fetchEmployeeOptions = useCallback(async () => {
    try {
      const rows = await safeRequestRows(
        UserServices.getHrReportEmployees({
          branch_id: activeBranch,
          department_id: selectedDepartment,
          designation_id: selectedDesignation,
          limit: 1000,
        })
      );

      const scopedEmployees = rows.filter(
        (employee) => !LOGIN_IDS.includes(parseInt(employee.id, 10))
      );

      setEmployeeOptions(scopedEmployees);

      if (
        selectedEmployee &&
        !scopedEmployees.some((employee) => String(employee.id) === String(selectedEmployee))
      ) {
        setSelectedEmployee("");
      }
    } catch (error) {
      console.error("Error loading employee options:", error);
      setEmployeeOptions([]);
    }
  }, [activeBranch, selectedDepartment, selectedDesignation, selectedEmployee]);

  const calculateLateInfo = (attendance, dutyHoursMap) => {
    const staffId = String(attendance.attendee_id);
    const dayName = moment(attendance.date).format("dddd");
    const dutyHours = dutyHoursMap[staffId]?.[dayName];

    if (!dutyHours?.start_time || !attendance.checkin_time_24h) {
      return {
        isLate: false,
        lateMinutes: 0,
        lateText: "On Time",
        dutyWindow: "N/A",
      };
    }

    const checkIn = moment(attendance.checkin_time_24h, "HH:mm:ss");
    const dutyStart = moment(dutyHours.start_time, "HH:mm");
    const dutyEnd = dutyHours.end_time ? moment(dutyHours.end_time, "HH:mm") : null;

    if (!checkIn.isValid() || !dutyStart.isValid()) {
      return {
        isLate: false,
        lateMinutes: 0,
        lateText: "On Time",
        dutyWindow: "N/A",
      };
    }

    const lateMinutes = checkIn.isAfter(dutyStart)
      ? checkIn.diff(dutyStart, "minutes")
      : 0;

    const lateText =
      lateMinutes > 0
        ? `${Math.floor(lateMinutes / 60) > 0 ? `${Math.floor(lateMinutes / 60)}h ` : ""}${
            lateMinutes % 60
          }m`
        : "On Time";

    return {
      isLate: lateMinutes > 0,
      lateMinutes,
      lateText,
      dutyWindow: `${formatDateTime(dutyHours.start_time, "HH:mm")} - ${
        dutyEnd?.isValid() ? dutyEnd.format("hh:mm A") : dutyHours.end_time || "N/A"
      }`,
    };
  };

  const generateReport = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    setWarningMessages([]);

    try {
      let reportStartDate = startDate;
      let reportEndDate = endDate;

      if (moment(reportStartDate).isAfter(reportEndDate)) {
        [reportStartDate, reportEndDate] = [reportEndDate, reportStartDate];
      }

      const employeeRows = await safeRequestRows(
        UserServices.getHrReportEmployees({
          branch_id: activeBranch,
          department_id: selectedDepartment,
          designation_id: selectedDesignation,
          limit: 1000,
        })
      );

      const scopedEmployees = employeeRows.filter(
        (employee) =>
          !LOGIN_IDS.includes(parseInt(employee.id, 10)) &&
          (!selectedEmployee || String(employee.id) === String(selectedEmployee))
      );

      const scopedEmployeeIds = new Set(scopedEmployees.map((employee) => String(employee.id)));
      const employeeMap = {};

      scopedEmployees.forEach((employee) => {
        employeeMap[String(employee.id)] = employee;
      });

      const payrollFilters = {
        startdate: reportStartDate,
        enddate: reportEndDate,
        trainer_id: selectedEmployee || "",
      };

      const attendanceFilters = {
        sdate: reportStartDate,
        edate: reportEndDate,
        start_time: startTime || null,
        end_time: endTime || null,
        gender: null,
      };

      const [
        dutyHourResult,
        attendanceResult,
        leaveResult,
        salaryResult,
        commissionResult,
        fineResult,
        advanceResult,
        loanResult,
        promotionResult,
      ] = await Promise.all([
        safeOptionalRows("Duty Hours", UserServices.getActiveEmployeHours(activeBranch, 1000, 1)),
        safeOptionalRows(
          "Attendance",
          UserServices.getEmployeeAttendence(
            activeBranch,
            attendanceFilters,
            selectedEmployee || null,
            1000,
            1
          )
        ),
        safeOptionalRows("Leaves", UserServices.getLeaveApplications(activeBranch, 1000, 1)),
        safeOptionalRows(
          "Payroll",
          UserServices.getHrSalaryRecords({
            branch_id: activeBranch,
            start_date: reportStartDate,
            end_date: reportEndDate,
            user_id: selectedEmployee || "",
            limit: 1000,
          })
        ),
        safeOptionalRows(
          "Commission",
          UserServices.getLatestStaffCommision(activeBranch, payrollFilters, 1000, 1)
        ),
        safeOptionalRows(
          "Fines",
          UserServices.getHrFinanceRecords({
            branch_id: activeBranch,
            user_id: selectedEmployee || "",
            category: "Fine",
            start_date: reportStartDate,
            end_date: reportEndDate,
            limit: 1000,
          })
        ),
        safeOptionalRows(
          "Advances",
          UserServices.getHrFinanceRecords({
            branch_id: activeBranch,
            user_id: selectedEmployee || "",
            category: "Advance",
            start_date: reportStartDate,
            end_date: reportEndDate,
            limit: 1000,
          })
        ),
        safeOptionalRows(
          "Loans",
          UserServices.getHrLoanRecords({
            branch_id: activeBranch,
            staff_id: selectedEmployee || "",
            start_date: reportStartDate,
            end_date: reportEndDate,
            limit: 1000,
          })
        ),
        safeOptionalRows("Promotions", UserServices.getStaffPromotions(activeBranch, 1000, 1)),
      ]);

      const warningLabels = [
        dutyHourResult,
        attendanceResult,
        leaveResult,
        salaryResult,
        commissionResult,
        fineResult,
        advanceResult,
        loanResult,
        promotionResult,
      ]
        .filter((result) => result.failed)
        .map((result) => result.label);

      setWarningMessages(warningLabels);

      const dutyHourRows = dutyHourResult.rows;
      const attendanceRowsRaw = attendanceResult.rows;
      const leaveRowsRaw = leaveResult.rows;
      const salaryRowsRaw = salaryResult.rows;
      const commissionRowsRaw = commissionResult.rows;
      const fineRowsRaw = fineResult.rows;
      const advanceRowsRaw = advanceResult.rows;
      const loanRowsRaw = loanResult.rows;
      const promotionRowsRaw = promotionResult.rows;

      const dutyHoursMap = buildDutyHoursMap(dutyHourRows);

      const attendanceRows = attendanceRowsRaw
        .filter((record) => scopedEmployeeIds.has(String(record.attendee_id)))
        .map((record) => {
          const employee = employeeMap[String(record.attendee_id)] || {};
          const lateInfo = calculateLateInfo(record, dutyHoursMap);

          return {
            id: record.id,
            employee_name:
              buildEmployeeName(employee, record.user_name || "N/A"),
            department: employee.department || "N/A",
            designation: employee.designation || "N/A",
            date: record.date,
            attendance_status: record.attendance_status || "N/A",
            checkin_time: formatDateTime(record.checkin_time_24h, "HH:mm:ss"),
            checkout_time: formatDateTime(record.checkout_time_24h, "HH:mm:ss"),
            working_hours: record.working_hours || "N/A",
            duty_window: lateInfo.dutyWindow,
            is_late: lateInfo.isLate,
            late_text: lateInfo.lateText,
          };
        })
        .sort((a, b) => moment(b.date).diff(moment(a.date)));

      const leaveRows = leaveRowsRaw
        .filter((record) => {
          const employeeId = String(record.user_id || record.attendee_id || "");
          if (!scopedEmployeeIds.has(employeeId)) return false;
          return (
            moment(record.leave_from_date).isSameOrBefore(reportEndDate) &&
            moment(record.leave_to_date).isSameOrAfter(reportStartDate)
          );
        })
        .map((record, index) => {
          const employeeId = String(record.user_id || record.attendee_id || "");
          const employee = employeeMap[employeeId] || {};

          return {
            id: record.id || `leave-${index}`,
            employee_name: buildEmployeeName(employee),
            department: employee.department || "N/A",
            designation: employee.designation || "N/A",
            leave_type: record.leave_type || "N/A",
            leave_from_date: record.leave_from_date,
            leave_to_date: record.leave_to_date,
            application_status: record.application_status || "N/A",
            leave_status: record.leave_status || "N/A",
            reason: record.reason || record.remarks || "N/A",
          };
        })
        .sort((a, b) => moment(b.leave_from_date).diff(moment(a.leave_from_date)));

      const salaryRows = salaryRowsRaw
        .filter((record) => scopedEmployeeIds.has(String(record.id)))
        .map((record) => {
          const salary = toNumber(record.salary);
          const commission = toNumber(record.commission?.commission);
          const reward = toNumber(record.reward);
          const advance = toNumber(record.advance);
          const fine = toNumber(record.fine);
          const loan = toNumber(record.loan);
          const cafe = toNumber(record.cafe);
          const detections = toNumber(record.detections);
          const medical = toNumber(record.medical);

          return {
            id: record.id,
            employee_name: record.name,
            department: record.department || "N/A",
            designation: record.designation || "N/A",
            salary,
            reward,
            commission,
            advance,
            fine,
            loan,
            cafe,
            detections,
            medical,
            net_payable:
              salary + commission + reward - advance - fine - loan - cafe - detections - medical,
          };
        })
        .sort((a, b) => a.employee_name.localeCompare(b.employee_name));

      const commissionRows = commissionRowsRaw
        .filter((record) => scopedEmployeeIds.has(String(record.id)))
        .map((record) => ({
          id: record.id,
          employee_name: record.name,
          department: record.department || "N/A",
          designation: record.designation || "N/A",
          commission_per: toNumber(record.commission?.commission_per),
          gross_commission: toNumber(
            record.commission?.gross_commission ?? record.commission?.commission
          ),
          paid_commission: toNumber(record.commission?.paid_commission),
          outstanding_commission: toNumber(record.commission?.outstanding_commission),
          pt_commission: toNumber(record.commission?.pt_commission),
          gx_commission: toNumber(record.commission?.gx_commission),
          small_pt_commission: toNumber(record.commission?.small_pt_commission),
          payout_status: record.commission?.payout_status || "unpaid",
          payout_date: record.commission?.payout_date || null,
        }))
        .sort((a, b) => a.employee_name.localeCompare(b.employee_name));

      const fineRows = fineRowsRaw
        .filter((record) => scopedEmployeeIds.has(String(record.user_id)))
        .map((record) => {
          const employee = employeeMap[String(record.user_id)] || {};
          return {
            id: record.id,
            employee_name: buildEmployeeName(
              employee,
              `${record.user_fname || ""} ${record.user_lname || ""}`.trim() || "N/A"
            ),
            department: employee.department || "N/A",
            designation: employee.designation || "N/A",
            amount: toNumber(record.amount),
            reason: record.reason || "N/A",
            occurrence_date: record.occurrence_date,
            return_month: record.return_month,
          };
        })
        .sort((a, b) => moment(b.occurrence_date).diff(moment(a.occurrence_date)));

      const advanceRows = advanceRowsRaw
        .filter((record) => scopedEmployeeIds.has(String(record.user_id)))
        .map((record) => {
          const employee = employeeMap[String(record.user_id)] || {};
          return {
            id: record.id,
            employee_name: buildEmployeeName(
              employee,
              `${record.user_fname || ""} ${record.user_lname || ""}`.trim() || "N/A"
            ),
            department: employee.department || "N/A",
            designation: employee.designation || "N/A",
            amount: toNumber(record.amount),
            transaction_type: record.transaction_type || "N/A",
            payment_method: record.payment_method || "N/A",
            reason: record.reason || "N/A",
            occurrence_date: record.occurrence_date,
            return_month: record.return_month,
          };
        })
        .sort((a, b) => moment(b.occurrence_date).diff(moment(a.occurrence_date)));

      const loanRows = loanRowsRaw
        .filter((record) => scopedEmployeeIds.has(String(record.staff_id)))
        .map((record) => {
          const employee = employeeMap[String(record.staff_id)] || {};
          const paidAmount = Array.isArray(record.installmentsDetail)
            ? record.installmentsDetail.reduce(
                (sum, installment) => sum + toNumber(installment.amount),
                0
              )
            : 0;

          return {
            id: record.id,
            employee_name: buildEmployeeName(employee, record.staff_name || "N/A"),
            department: employee.department || "N/A",
            designation: employee.designation || "N/A",
            amount: toNumber(record.amount),
            installment: toNumber(record.installment),
            paid_amount: paidAmount,
            pending_amount: Math.max(0, toNumber(record.amount) - paidAmount),
            reason: record.reason || "N/A",
            date: record.date,
            return_start_date: record.return_start_date,
          };
        })
        .sort((a, b) => moment(b.date).diff(moment(a.date)));

      const promotionRows = promotionRowsRaw
        .filter((record) => scopedEmployeeIds.has(String(record.employee_id)))
        .map((record) => ({
          id: record.id,
          employee_name: record.employee || "N/A",
          previous_department: record.pervs_depart || "N/A",
          new_department: record.new_depart || "N/A",
          previous_designation: record.pervs_designation || "N/A",
          new_designation: record.new_designation || "N/A",
          previous_salary: toNumber(record.previous_salary),
          new_salary: toNumber(record.new_salary),
          status: String(record.status) === "1" ? "Active" : "Inactive",
        }))
        .sort((a, b) => a.employee_name.localeCompare(b.employee_name));

      const employeeLedgerRows = selectedEmployee
        ? [
            ...attendanceRows.map((row) => ({
              record_key: `attendance-${row.id}`,
              event_date: row.date,
              module: "Attendance",
              event: row.is_late ? "Late Check In" : row.attendance_status,
              amount: "",
              status: row.attendance_status,
              detail: `${row.checkin_time} / ${row.checkout_time}`,
              note: `Duty ${row.duty_window} | Late ${row.late_text}`,
            })),
            ...leaveRows.map((row) => ({
              record_key: `leave-${row.id}`,
              event_date: row.leave_from_date,
              module: "Leave",
              event: row.leave_type,
              amount: "",
              status: row.application_status,
              detail: `${formatDate(row.leave_from_date)} to ${formatDate(row.leave_to_date)}`,
              note: row.reason,
            })),
            ...salaryRows.map((row) => ({
              record_key: `salary-${row.id}`,
              event_date: reportEndDate,
              module: "Payroll",
              event: "Net Salary Prepared",
              amount: row.net_payable,
              status: "Calculated",
              detail: `Base ${formatCurrency(row.salary)} | Commission ${formatCurrency(
                row.commission
              )}`,
              note: `Advance ${formatCurrency(row.advance)} | Fine ${formatCurrency(
                row.fine
              )} | Loan ${formatCurrency(row.loan)}`,
            })),
            ...commissionRows.map((row) => ({
              record_key: `commission-${row.id}`,
              event_date: row.payout_date || reportEndDate,
              module: "Commission",
              event: "Commission Summary",
              amount: row.gross_commission,
              status: row.payout_status,
              detail: `Paid ${formatCurrency(row.paid_commission)} | Outstanding ${formatCurrency(
                row.outstanding_commission
              )}`,
              note: `PT ${formatCurrency(row.pt_commission)} | GX ${formatCurrency(
                row.gx_commission
              )} | SPT ${formatCurrency(row.small_pt_commission)}`,
            })),
            ...fineRows.map((row) => ({
              record_key: `fine-${row.id}`,
              event_date: row.occurrence_date,
              module: "Fine",
              event: "Fine Applied",
              amount: row.amount,
              status: "Deduction",
              detail: `Recovery ${formatDate(row.return_month)}`,
              note: row.reason,
            })),
            ...advanceRows.map((row) => ({
              record_key: `advance-${row.id}`,
              event_date: row.occurrence_date,
              module: "Advance",
              event: row.transaction_type,
              amount: row.amount,
              status: row.payment_method,
              detail: `Recovery ${formatDate(row.return_month)}`,
              note: row.reason,
            })),
            ...loanRows.map((row) => ({
              record_key: `loan-${row.id}`,
              event_date: row.date,
              module: "Loan",
              event: "Loan Issued",
              amount: row.amount,
              status: "Active",
              detail: `Installment ${formatCurrency(row.installment)} | Outstanding ${formatCurrency(
                row.pending_amount
              )}`,
              note: row.reason,
            })),
            ...promotionRows.map((row) => ({
              record_key: `promotion-${row.id}`,
              event_date: reportEndDate,
              module: "Promotion",
              event: `${row.previous_designation} -> ${row.new_designation}`,
              amount: row.new_salary,
              status: row.status,
              detail: `${row.previous_department} -> ${row.new_department}`,
              note: `Salary ${formatCurrency(row.previous_salary)} -> ${formatCurrency(
                row.new_salary
              )}`,
            })),
          ].sort((a, b) => {
            const firstDate = a.event_date ? moment(a.event_date) : moment.invalid();
            const secondDate = b.event_date ? moment(b.event_date) : moment.invalid();

            if (!firstDate.isValid() && !secondDate.isValid()) return 0;
            if (!firstDate.isValid()) return 1;
            if (!secondDate.isValid()) return -1;
            return secondDate.valueOf() - firstDate.valueOf();
          })
        : [];

      setReportData({
        employees: scopedEmployees,
        attendanceRows,
        leaveRows,
        salaryRows,
        commissionRows,
        fineRows,
        advanceRows,
        loanRows,
        promotionRows,
        employeeLedgerRows,
        summary: {
          employees: scopedEmployees.length,
          present: attendanceRows.filter((record) => record.attendance_status === "Present").length,
          late: attendanceRows.filter((record) => record.is_late).length,
          absent: attendanceRows.filter((record) => record.attendance_status === "Absent").length,
          onLeave: leaveRows.filter((record) => record.application_status === "Approved").length,
          netSalary: salaryRows.reduce((sum, row) => sum + row.net_payable, 0),
          grossCommission: commissionRows.reduce(
            (sum, row) => sum + row.gross_commission,
            0
          ),
          fines: fineRows.reduce((sum, row) => sum + row.amount, 0),
          advances: advanceRows.reduce((sum, row) => sum + row.amount, 0),
          loanOutstanding: loanRows.reduce((sum, row) => sum + row.pending_amount, 0),
        },
      });
    } catch (error) {
      console.error("Error generating HR report:", error);
      setErrorMessage(
        "Unable to generate the HR report right now. Please review the filters and try again."
      );
      setReportData(emptyReportState);
    } finally {
      setLoading(false);
    }
  }, [
    activeBranch,
    endDate,
    selectedDepartment,
    selectedDesignation,
    selectedEmployee,
    startDate,
    startTime,
    endTime,
  ]);

  const handleReset = () => {
    setSelectedBranch(branch_id || "");
    setSelectedDepartment("");
    setSelectedDesignation("");
    setSelectedEmployee("");
    setStartDate(moment().startOf("month").format("YYYY-MM-DD"));
    setEndDate(moment().format("YYYY-MM-DD"));
    setStartTime("");
    setEndTime("");
  };

  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value).replace(/"/g, "\"\"");
    return /[",\n]/.test(stringValue) ? `"${stringValue}"` : stringValue;
  };

  const downloadCsv = (rows) => {
    if (!rows.length) return;

    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((row) =>
        headers.map((header) => escapeCsvValue(row[header])).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `hr-report-${moment().format("YYYYMMDD-HHmmss")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = () => {
    const exportRows = [];

    reportData.employees.forEach((employee) => {
      exportRows.push({
        section: "Employee Profile",
        employee_name: `${employee.first_name || ""} ${employee.last_name || ""}`.trim(),
        date: "",
        amount: "",
        status: employee.employment_status || "N/A",
        detail_1: employee.department || "N/A",
        detail_2: employee.designation || "N/A",
        detail_3: employee.phone || "N/A",
      });
    });

    reportData.attendanceRows.forEach((row) => {
      exportRows.push({
        section: "Attendance",
        employee_name: row.employee_name,
        date: row.date,
        amount: "",
        status: row.attendance_status,
        detail_1: row.duty_window,
        detail_2: row.checkin_time,
        detail_3: row.is_late ? row.late_text : "On Time",
      });
    });

    reportData.leaveRows.forEach((row) => {
      exportRows.push({
        section: "Leave",
        employee_name: row.employee_name,
        date: row.leave_from_date,
        amount: "",
        status: row.application_status,
        detail_1: row.leave_type,
        detail_2: row.leave_to_date,
        detail_3: row.reason,
      });
    });

    reportData.salaryRows.forEach((row) => {
      exportRows.push({
        section: "Salary",
        employee_name: row.employee_name,
        date: "",
        amount: row.net_payable,
        status: "Net Payable",
        detail_1: row.salary,
        detail_2: row.commission,
        detail_3: row.detections,
      });
    });

    reportData.commissionRows.forEach((row) => {
      exportRows.push({
        section: "Commission",
        employee_name: row.employee_name,
        date: row.payout_date || "",
        amount: row.gross_commission,
        status: row.payout_status,
        detail_1: row.paid_commission,
        detail_2: row.outstanding_commission,
        detail_3: row.commission_per,
      });
    });

    reportData.fineRows.forEach((row) => {
      exportRows.push({
        section: "Fine",
        employee_name: row.employee_name,
        date: row.occurrence_date,
        amount: row.amount,
        status: "Fine",
        detail_1: row.return_month,
        detail_2: row.reason,
        detail_3: "",
      });
    });

    reportData.advanceRows.forEach((row) => {
      exportRows.push({
        section: "Advance",
        employee_name: row.employee_name,
        date: row.occurrence_date,
        amount: row.amount,
        status: row.transaction_type,
        detail_1: row.payment_method,
        detail_2: row.return_month,
        detail_3: row.reason,
      });
    });

    reportData.loanRows.forEach((row) => {
      exportRows.push({
        section: "Loan",
        employee_name: row.employee_name,
        date: row.date,
        amount: row.amount,
        status: "Loan",
        detail_1: row.installment,
        detail_2: row.paid_amount,
        detail_3: row.pending_amount,
      });
    });

    reportData.promotionRows.forEach((row) => {
      exportRows.push({
        section: "Promotion",
        employee_name: row.employee_name,
        date: "",
        amount: row.new_salary,
        status: row.status,
        detail_1: row.previous_designation,
        detail_2: row.new_designation,
        detail_3: row.new_department,
      });
    });

    downloadCsv(exportRows);
  };

  const handlePrint = () => {
    window.print();
  };

  const selectedEmployeeAttendance = selectedEmployee ? reportData.attendanceRows : [];
  const selectedEmployeeLeaves = selectedEmployee ? reportData.leaveRows : [];
  const selectedEmployeePayroll = selectedEmployee ? reportData.salaryRows[0] || null : null;
  const selectedEmployeeCommission = selectedEmployee ? reportData.commissionRows[0] || null : null;
  const selectedEmployeeFineTotal = selectedEmployee
    ? reportData.fineRows.reduce((sum, row) => sum + row.amount, 0)
    : 0;
  const selectedEmployeeAdvanceTotal = selectedEmployee
    ? reportData.advanceRows.reduce((sum, row) => sum + row.amount, 0)
    : 0;
  const selectedEmployeeLoanOutstanding = selectedEmployee
    ? reportData.loanRows.reduce((sum, row) => sum + row.pending_amount, 0)
    : 0;

  useEffect(() => {
    if (!branch_id) return;
    setSelectedBranch(branch_id);
  }, [branch_id]);

  useEffect(() => {
    fetchLookups();
  }, []);

  useEffect(() => {
    fetchEmployeeOptions();
  }, [fetchEmployeeOptions]);

  useEffect(() => {
    if (!startDate || !endDate) return;

    const timer = setTimeout(() => {
      generateReport();
    }, 300);

    return () => clearTimeout(timer);
  }, [
    activeBranch,
    endDate,
    generateReport,
    selectedDepartment,
    selectedDesignation,
    selectedEmployee,
    startDate,
    startTime,
    endTime,
  ]);

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <style>
            {`
              @media print {
                .hr-report-no-print {
                  display: none !important;
                }

                .page-content,
                .site-main-design,
                .card {
                  padding: 0 !important;
                  margin: 0 !important;
                  box-shadow: none !important;
                }

                .hr-report-print-break {
                  page-break-before: always;
                }
              }
            `}
          </style>
          <div className="hr-report-no-print">
            <MenuButton />
          </div>

          <div className="card radius-10 custom-shadow mb-4 border-0 overflow-hidden">
            <div
              className="card-body p-4"
              style={{ background: "linear-gradient(135deg, #f8fbff 0%, #eef6ff 100%)" }}
            >
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                <div>
                  <h3 className="mb-1">Detailed HR Report</h3>
                  <p className="text-muted mb-0">
                    Employee-wise and combined HR reporting for attendance, leave, payroll, fines,
                    advances, commissions, loans, and movement history.
                  </p>
                </div>
                <div className="text-md-end">
                  <div className="small text-muted">Generated</div>
                  <div className="fw-semibold">{moment().format("DD MMM YYYY, hh:mm A")}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card radius-10 custom-shadow mb-4 hr-report-no-print">
            <div className="card-header bg-white">
              <h5 className="mb-0">Report Filters</h5>
            </div>
            <div className="card-body">
              <div className="alert alert-light border mb-3">
                The report refreshes automatically when you change branch, employee, department,
                designation, date range, or time range.
              </div>
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">Branch</label>
                  <select
                    className="form-select"
                    value={selectedBranch}
                    onChange={(e) => setSelectedBranch(e.target.value)}
                  >
                    <option value="">All Branches</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Department</label>
                  <select
                    className="form-select"
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                  >
                    <option value="">All Departments</option>
                    {departments.map((department) => (
                      <option key={department.id} value={department.id}>
                        {department.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Designation</label>
                  <select
                    className="form-select"
                    value={selectedDesignation}
                    onChange={(e) => setSelectedDesignation(e.target.value)}
                  >
                    <option value="">All Designations</option>
                    {designations.map((designation) => (
                      <option key={designation.id} value={designation.id}>
                        {designation.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Employee</label>
                  <select
                    className="form-select"
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    disabled={filtersLoading}
                  >
                    <option value="">All Employees</option>
                    {employeeOptions.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {`${employee.first_name || ""} ${employee.last_name || ""}`.trim()}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Start Time</label>
                  <input
                    type="time"
                    className="form-control"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">End Time</label>
                  <input
                    type="time"
                    className="form-control"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2 mt-4">
                <button className="btn btn-primary" onClick={generateReport} disabled={loading}>
                  {loading ? "Generating..." : "Generate Report"}
                </button>
                <button className="btn btn-outline-secondary" onClick={handleReset} disabled={loading}>
                  Reset Filters
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={handleExportCsv}
                  disabled={loading || reportData.employees.length === 0}
                >
                  Export CSV
                </button>
                <button
                  className="btn btn-outline-dark"
                  onClick={handlePrint}
                  disabled={loading || reportData.employees.length === 0}
                >
                  Print
                </button>
              </div>
            </div>
          </div>

          {errorMessage ? <div className="alert alert-danger">{errorMessage}</div> : null}
          {warningMessages.length ? (
            <div className="alert alert-warning">
              Some HR sections could not be loaded for this report: {warningMessages.join(", ")}.
              The remaining data is still shown below.
            </div>
          ) : null}

          {loading ? (
            <div className="card radius-10 custom-shadow mb-4">
              <div className="card-body">
                <Skeleton height={180} />
                <Skeleton count={6} height={30} className="mt-3" />
              </div>
            </div>
          ) : null}

          {!loading && selectedEmployeeProfile ? (
            <div className="card radius-10 custom-shadow mb-4">
              <div className="card-header bg-white border-bottom">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h5 className="mb-0">Employee Profile Snapshot</h5>
                  <span className="badge bg-primary">
                    {`${selectedEmployeeProfile.first_name || ""} ${
                      selectedEmployeeProfile.last_name || ""
                    }`.trim()}
                  </span>
                </div>
              </div>
              <div className="card-body">
                <div className="row">
                  <DetailItem label="Employee ID" value={selectedEmployeeProfile.uid} />
                  <DetailItem
                    label="Full Name"
                    value={`${selectedEmployeeProfile.first_name || ""} ${
                      selectedEmployeeProfile.last_name || ""
                    }`.trim()}
                  />
                  <DetailItem label="Branch" value={selectedEmployeeProfile.branch_name} />
                  <DetailItem label="Department" value={selectedEmployeeProfile.department} />
                  <DetailItem label="Designation" value={selectedEmployeeProfile.designation} />
                  <DetailItem label="Salary" value={formatCurrency(selectedEmployeeProfile.salary)} />
                  <DetailItem label="Commission %" value={selectedEmployeeProfile.commission || 0} />
                  <DetailItem label="Joining Date" value={formatDate(selectedEmployeeProfile.joining)} />
                  <DetailItem label="Date of Birth" value={formatDate(selectedEmployeeProfile.dob)} />
                  <DetailItem label="Father Name" value={selectedEmployeeProfile.father_name} />
                  <DetailItem label="Phone" value={selectedEmployeeProfile.phone} />
                  <DetailItem label="Email" value={selectedEmployeeProfile.email} />
                  <DetailItem label="Official Email" value={selectedEmployeeProfile.official_email} />
                  <DetailItem label="CNIC" value={selectedEmployeeProfile.cnic} />
                  <DetailItem label="Employment Status" value={selectedEmployeeProfile.employment_status} />
                  <DetailItem
                    label="Appointment Date"
                    value={formatDate(selectedEmployeeProfile.appointment_date)}
                  />
                  <DetailItem label="Probation Duration" value={selectedEmployeeProfile.probation_duration} />
                  <DetailItem
                    label="Confirmation Date"
                    value={formatDate(selectedEmployeeProfile.confirmation_date)}
                  />
                  <DetailItem
                    label="Employment End Date"
                    value={formatDate(selectedEmployeeProfile.employment_end_date)}
                  />
                  <DetailItem
                    label="Emergency Contact"
                    value={selectedEmployeeProfile.emergency_contact_no}
                  />
                  <DetailItem label="Blood Group" value={selectedEmployeeProfile.blood_group} />
                  <DetailItem
                    label="Monthly Medical"
                    value={formatCurrency(selectedEmployeeProfile.monthly_medical)}
                  />
                  <DetailItem label="Address" value={selectedEmployeeProfile.address} />
                  <DetailItem label="City" value={selectedEmployeeProfile.city} />
                  <DetailItem label="Country" value={selectedEmployeeProfile.country} />
                </div>
              </div>
            </div>
          ) : null}

          {!loading ? (
            <div className="card radius-10 custom-shadow mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
                  <div>
                    <h5 className="mb-1">Report Scope</h5>
                    <p className="text-muted mb-0">
                      The report below is prepared from the current HR data sources and filters.
                    </p>
                  </div>
                  <div className="small text-muted">
                    Period: {formatDate(reportRangeStart)} to {formatDate(reportRangeEnd)}
                  </div>
                </div>
                <div className="row g-3">
                  <div className="col-md-3">
                    <ScopePill label="Branch" value={selectedBranchLabel} />
                  </div>
                  <div className="col-md-3">
                    <ScopePill label="Department" value={selectedDepartmentLabel} />
                  </div>
                  <div className="col-md-3">
                    <ScopePill label="Designation" value={selectedDesignationLabel} />
                  </div>
                  <div className="col-md-3">
                    <ScopePill label="Employee" value={selectedEmployeeLabel} />
                  </div>
                  <div className="col-md-3">
                    <ScopePill label="From Time" value={startTime || "All Times"} />
                  </div>
                  <div className="col-md-3">
                    <ScopePill label="To Time" value={endTime || "All Times"} />
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {!loading ? (
            <div className="row">
              <SummaryTile label="Employees in Scope" value={reportData.summary.employees} tone="primary" />
              <SummaryTile label="Present Marks" value={reportData.summary.present} tone="success" />
              <SummaryTile label="Late Marks" value={reportData.summary.late} tone="warning" />
              <SummaryTile label="Absent Marks" value={reportData.summary.absent} tone="danger" />
              <SummaryTile label="Approved Leaves" value={reportData.summary.onLeave} tone="info" />
              <SummaryTile label="Net Salary" value={formatCurrency(reportData.summary.netSalary)} tone="dark" />
              <SummaryTile
                label="Gross Commission"
                value={formatCurrency(reportData.summary.grossCommission)}
                tone="secondary"
              />
              <SummaryTile label="Fines" value={formatCurrency(reportData.summary.fines)} tone="danger" />
              <SummaryTile label="Advances" value={formatCurrency(reportData.summary.advances)} tone="warning" />
              <SummaryTile
                label="Loan Outstanding"
                value={formatCurrency(reportData.summary.loanOutstanding)}
                tone="primary"
              />
            </div>
          ) : null}

          {!loading && selectedEmployeeProfile ? (
            <div className="card radius-10 custom-shadow mb-4">
              <div className="card-header bg-white border-bottom">
                <h5 className="mb-0">Employee 360 HR Summary</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <SummaryTile
                    label="Present Marks"
                    value={
                      selectedEmployeeAttendance.filter(
                        (row) => row.attendance_status === "Present"
                      ).length
                    }
                    tone="success"
                  />
                  <SummaryTile
                    label="Late Marks"
                    value={selectedEmployeeAttendance.filter((row) => row.is_late).length}
                    tone="warning"
                  />
                  <SummaryTile
                    label="Approved Leaves"
                    value={
                      selectedEmployeeLeaves.filter(
                        (row) => row.application_status === "Approved"
                      ).length
                    }
                    tone="info"
                  />
                  <SummaryTile
                    label="Net Salary"
                    value={formatCurrency(selectedEmployeePayroll?.net_payable || 0)}
                    tone="dark"
                  />
                  <SummaryTile
                    label="Gross Commission"
                    value={formatCurrency(selectedEmployeeCommission?.gross_commission || 0)}
                    tone="secondary"
                  />
                  <SummaryTile
                    label="Outstanding Loan"
                    value={formatCurrency(selectedEmployeeLoanOutstanding)}
                    tone="primary"
                  />
                  <SummaryTile
                    label="Fines"
                    value={formatCurrency(selectedEmployeeFineTotal)}
                    tone="danger"
                  />
                  <SummaryTile
                    label="Advances"
                    value={formatCurrency(selectedEmployeeAdvanceTotal)}
                    tone="warning"
                  />
                </div>
              </div>
            </div>
          ) : null}

          {!loading && !selectedEmployee ? (
            <div className="alert alert-info">
              Select a single employee to see the full 360 profile and combined employee ledger in
              one place.
            </div>
          ) : null}

          {!loading ? (
            <>
              <ReportTableCard
                title="Selected Employee Combined Ledger"
                tone="primary"
                rows={reportData.employeeLedgerRows}
                emptyMessage="Select a single employee to prepare one combined HR ledger with attendance, leave, payroll, commission, fines, advances, loans, and promotions."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  { label: "Date", render: (row) => formatDate(row.event_date) },
                  { label: "Module", key: "module" },
                  { label: "Event", key: "event" },
                  {
                    label: "Amount",
                    render: (row) => (row.amount ? formatCurrency(row.amount) : "N/A"),
                  },
                  { label: "Status", key: "status" },
                  { label: "Detail", key: "detail" },
                  { label: "Note", key: "note" },
                ]}
              />

              <ReportTableCard
                title="Employee Scope Register"
                tone="primary"
                rows={reportData.employees}
                emptyMessage="No employees matched the selected report filters."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  {
                    label: "Employee",
                    render: (row) =>
                      `${row.first_name || ""} ${row.last_name || ""}`.trim() || "N/A",
                  },
                  { label: "Employee ID", key: "uid" },
                  { label: "Branch", key: "branch_name" },
                  { label: "Department", key: "department" },
                  { label: "Designation", key: "designation" },
                  { label: "Salary", render: (row) => formatCurrency(row.salary) },
                  { label: "Commission %", key: "commission" },
                  { label: "Joining", render: (row) => formatDate(row.joining) },
                  { label: "Phone", key: "phone" },
                ]}
              />

              <ReportTableCard
                title="Attendance Register"
                tone="success"
                rows={reportData.attendanceRows}
                emptyMessage="No attendance records found for the selected criteria."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  { label: "Employee", key: "employee_name" },
                  { label: "Date", render: (row) => formatDate(row.date) },
                  { label: "Department", key: "department" },
                  { label: "Designation", key: "designation" },
                  { label: "Duty Hours", key: "duty_window" },
                  { label: "Check In", key: "checkin_time" },
                  { label: "Check Out", key: "checkout_time" },
                  { label: "Working Hours", key: "working_hours" },
                  {
                    label: "Status",
                    render: (row) => (
                      <span
                        className={`badge bg-${
                          row.is_late ? "warning" : row.attendance_status === "Absent" ? "danger" : "success"
                        }`}
                      >
                        {row.is_late ? "Late" : row.attendance_status}
                      </span>
                    ),
                  },
                  { label: "Late By", key: "late_text" },
                ]}
              />

              <ReportTableCard
                title="Leave Register"
                tone="info"
                rows={reportData.leaveRows}
                emptyMessage="No leave records matched the selected date range."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  { label: "Employee", key: "employee_name" },
                  { label: "Department", key: "department" },
                  { label: "Designation", key: "designation" },
                  { label: "Type", key: "leave_type" },
                  { label: "From", render: (row) => formatDate(row.leave_from_date) },
                  { label: "To", render: (row) => formatDate(row.leave_to_date) },
                  { label: "Leave Status", key: "leave_status" },
                  {
                    label: "Approval",
                    render: (row) => (
                      <span
                        className={`badge bg-${
                          row.application_status === "Approved"
                            ? "success"
                            : row.application_status === "Rejected"
                            ? "danger"
                            : "warning"
                        }`}
                      >
                        {row.application_status}
                      </span>
                    ),
                  },
                  { label: "Reason", key: "reason" },
                ]}
              />

              <ReportTableCard
                title="Payroll Register"
                tone="dark"
                rows={reportData.salaryRows}
                emptyMessage="No payroll records were returned for this report scope."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  { label: "Employee", key: "employee_name" },
                  { label: "Department", key: "department" },
                  { label: "Designation", key: "designation" },
                  { label: "Base Salary", render: (row) => formatCurrency(row.salary) },
                  { label: "Commission", render: (row) => formatCurrency(row.commission) },
                  { label: "Reward", render: (row) => formatCurrency(row.reward) },
                  { label: "Advance", render: (row) => formatCurrency(row.advance) },
                  { label: "Fine", render: (row) => formatCurrency(row.fine) },
                  { label: "Loan", render: (row) => formatCurrency(row.loan) },
                  { label: "Deduction", render: (row) => formatCurrency(row.detections) },
                  { label: "Net Payable", render: (row) => formatCurrency(row.net_payable) },
                ]}
              />

              <ReportTableCard
                title="Commission Register"
                tone="secondary"
                rows={reportData.commissionRows}
                emptyMessage="No commission records were returned for this report scope."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  { label: "Employee", key: "employee_name" },
                  { label: "Department", key: "department" },
                  { label: "Designation", key: "designation" },
                  { label: "Commission %", key: "commission_per" },
                  { label: "Gross", render: (row) => formatCurrency(row.gross_commission) },
                  { label: "Paid", render: (row) => formatCurrency(row.paid_commission) },
                  { label: "Outstanding", render: (row) => formatCurrency(row.outstanding_commission) },
                  { label: "PT", render: (row) => formatCurrency(row.pt_commission) },
                  { label: "GX", render: (row) => formatCurrency(row.gx_commission) },
                  { label: "SPT", render: (row) => formatCurrency(row.small_pt_commission) },
                  {
                    label: "Payout",
                    render: (row) => (
                      <span className={`badge bg-${row.payout_status === "paid" ? "success" : "warning"}`}>
                        {row.payout_status}
                      </span>
                    ),
                  },
                ]}
              />

              <ReportTableCard
                title="Fine Register"
                tone="danger"
                rows={reportData.fineRows}
                emptyMessage="No fines were found for the selected scope."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  { label: "Employee", key: "employee_name" },
                  { label: "Department", key: "department" },
                  { label: "Designation", key: "designation" },
                  { label: "Amount", render: (row) => formatCurrency(row.amount) },
                  { label: "Occurrence Date", render: (row) => formatDate(row.occurrence_date) },
                  { label: "Return Month", render: (row) => formatDate(row.return_month) },
                  { label: "Reason", key: "reason" },
                ]}
              />

              <ReportTableCard
                title="Advance Register"
                tone="warning"
                rows={reportData.advanceRows}
                emptyMessage="No advances were found for the selected scope."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  { label: "Employee", key: "employee_name" },
                  { label: "Department", key: "department" },
                  { label: "Designation", key: "designation" },
                  { label: "Amount", render: (row) => formatCurrency(row.amount) },
                  { label: "Type", key: "transaction_type" },
                  { label: "Payment Method", key: "payment_method" },
                  { label: "Occurrence Date", render: (row) => formatDate(row.occurrence_date) },
                  { label: "Recovery Month", render: (row) => formatDate(row.return_month) },
                  { label: "Reason", key: "reason" },
                ]}
              />

              <ReportTableCard
                title="Loan Register"
                tone="primary"
                rows={reportData.loanRows}
                emptyMessage="No loans were found for the selected scope."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  { label: "Employee", key: "employee_name" },
                  { label: "Department", key: "department" },
                  { label: "Designation", key: "designation" },
                  { label: "Loan Amount", render: (row) => formatCurrency(row.amount) },
                  { label: "Installment", render: (row) => formatCurrency(row.installment) },
                  { label: "Paid", render: (row) => formatCurrency(row.paid_amount) },
                  { label: "Outstanding", render: (row) => formatCurrency(row.pending_amount) },
                  { label: "Issue Date", render: (row) => formatDate(row.date) },
                  { label: "Recovery Start", render: (row) => formatDate(row.return_start_date) },
                  { label: "Reason", key: "reason" },
                ]}
              />

              <ReportTableCard
                title="Promotion History"
                tone="dark"
                rows={reportData.promotionRows}
                emptyMessage="No promotion history was found in the selected HR scope."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  { label: "Employee", key: "employee_name" },
                  { label: "Previous Department", key: "previous_department" },
                  { label: "New Department", key: "new_department" },
                  { label: "Previous Designation", key: "previous_designation" },
                  { label: "New Designation", key: "new_designation" },
                  { label: "Previous Salary", render: (row) => formatCurrency(row.previous_salary) },
                  { label: "New Salary", render: (row) => formatCurrency(row.new_salary) },
                  { label: "Status", key: "status" },
                ]}
              />
            </>
          ) : null}
        </div>
      </main>
    </div>
  );
};

export default DetailedHRReport;
